import type { LayerTypesVersionId } from '@festina/configuration-general-types/configuration'
import {
  type BranchKey,
  configurationKeys,
  useCreateOrUpdateConciseBranch,
  useInvalidateAllConciseBranches,
} from '@festina-life/api'
import { ErrorMessage } from '@festina-life/components'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { FormikForm } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useClose } from '@festina-life/ui/structure'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { fetchMetaModelKeyPrefix } from '../../../uiModels/api/keys'
import { useConciseBranch } from '../../../uiModels/branches/useConciseBranch'
import type { BranchTypesInput } from '../../../uiModels/branchTypes/types'
import { useCreateTypesVersionFromFile } from '../../../uiModels/branchTypes/useCreateTypesVersionFromFile'
import { BranchTypeFormInputs } from './BranchTypeFormInputs'
import { useBranchTypesValidationSchema } from './useBranchTypesValidationSchema'

type Props = {
  branchKey: BranchKey
}

export const BranchTypesForm = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const conciseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: branchKey.name,
    shouldFallbackToStandard: false,
  })
  const validationSchema = useBranchTypesValidationSchema()
  const {
    mutateAsync: updateConciseBranch,
    isPending: isUpdatingConciseBranch,
  } = useCreateOrUpdateConciseBranch()
  const invalidateBranches = useInvalidateAllConciseBranches()
  const { mutateAsync: createTypesVersion, isPending: isCreatingTypesVersion } =
    useCreateTypesVersionFromFile()
  const [isInvalidatingBranches, setIsInvalidatingBranches] = useState(false)
  const queryClient = useQueryClient()
  const close = useClose()
  const [error, setError] = useState<Optional<Error>>()
  const initialValues = {
    typesVersionName: '',
    typesVersionDescription: '',
    typeVersionFile: [],
    server: branchKey.server,
  } satisfies BranchTypesInput

  assertNotEmpty(conciseBranch)

  const handleSubmitBranchTypes = async (values: BranchTypesInput) => {
    try {
      assertNotEmpty(conciseBranch.branchCategory)

      const createdTypeVersions = await createTypesVersion(values)
      let typeVersions: Optional<LayerTypesVersionId[]>

      if (createdTypeVersions && Array.isArray(createdTypeVersions)) {
        typeVersions = createdTypeVersions.map(([layerId, typesVersionId]) => ({
          _type: 'LayerTypesVersionId',
          typesVersionId,
          layer: layerId,
        }))
      } else if (createdTypeVersions) {
        // If we get a non layered type package we simply assign the same types to all layers
        typeVersions = [
          {
            _type: 'LayerTypesVersionId',
            typesVersionId: createdTypeVersions,
            layer: null,
          },
        ]
      }

      await updateConciseBranch({
        typeVersions,
        branchName: conciseBranch.name ?? '',
        baseBranchName: conciseBranch.baseBranchName,
        server: branchKey.server,
        category: conciseBranch.branchCategory,
        followsBranchName: conciseBranch.followsBranchName,
      })

      setIsInvalidatingBranches(true)

      await invalidateBranches()

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: fetchMetaModelKeyPrefix({
            server: branchKey.server,
            branch: conciseBranch.name,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: [
            configurationKeys.fetchAllWorkspacesOnServerErrorsKeyPrefix({
              server: branchKey.server,
            }),
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            configurationKeys.fetchBranchAllErrorsPrefix({
              branchName: conciseBranch.name ?? '',
              server: branchKey.server,
            }),
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [configurationKeys.allConciseBranchesKeyPrefix],
        }),
      ])

      close()
    } catch (error) {
      setError(error)
    } finally {
      setIsInvalidatingBranches(false)
    }
  }

  return (
    <FormikForm
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitBranchTypes}
    >
      {error && (
        <ErrorMessage
          title={translate('branchTypesForm.updateError')}
          error={error}
        />
      )}
      <BranchTypeFormInputs />
      <div className="flex justify-end">
        <Button
          type="submit"
          isLoading={
            isUpdatingConciseBranch ||
            isCreatingTypesVersion ||
            isInvalidatingBranches
          }
        >
          {translate('update')}
        </Button>
      </div>
    </FormikForm>
  )
}
